import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, EnvelopeIcon, EnvelopeOpenIcon } from '@heroicons/react/24/outline';
import { useRelevamientoNotifications } from '../hooks/useRelevamientoNotifications';
import {
  notificationLocation,
  notificationTitle,
  relativeTime,
} from '../utils/relevamientoNotifications';

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    items,
    loading,
    permissionsLoading,
    canViewRelevamientos,
    unreadCount,
    isRead,
    markRead,
    markUnread,
    refresh,
  } = useRelevamientoNotifications();
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0;

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (!canViewRelevamientos) {
    return (
      <div className="card p-8 text-center">
        <BellIcon className="w-10 h-10 mx-auto text-gray-400 mb-3" />
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Sin acceso</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          No tenés permiso para ver notificaciones de relevamientos.
        </p>
      </div>
    );
  }

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(items.map((item) => item.id)));
  };

  const handleMarkRead = () => {
    if (!someSelected) return;
    markRead(selectedIds);
    setSelected(new Set());
  };

  const handleMarkUnread = () => {
    if (!someSelected) return;
    markUnread(selectedIds);
    setSelected(new Set());
  };

  const openItem = (id: number) => {
    markRead([id]);
    navigate(`/relevamientos/${id}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Notificaciones</h1>
          <p className="text-gray-600 dark:text-gray-300">
            Relevamientos abiertos del alcance actual
            {unreadCount > 0 ? ` · ${unreadCount} sin leer` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => refresh()}
          className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          Actualizar
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              checked={allSelected}
              onChange={toggleAll}
              disabled={items.length === 0}
            />
            Seleccionar todas
          </label>
          <div className="flex flex-wrap gap-2 ml-auto">
            <button
              type="button"
              onClick={handleMarkRead}
              disabled={!someSelected}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-white dark:hover:bg-gray-700"
            >
              <EnvelopeOpenIcon className="w-4 h-4" />
              Marcar leídas
            </button>
            <button
              type="button"
              onClick={handleMarkUnread}
              disabled={!someSelected}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-white dark:hover:bg-gray-700"
            >
              <EnvelopeIcon className="w-4 h-4" />
              Marcar no leídas
            </button>
          </div>
        </div>

        {loading && items.length === 0 ? (
          <div className="p-4 space-y-3 animate-pulse">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-14 bg-gray-100 dark:bg-gray-700 rounded" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="px-4 py-16 text-sm text-center text-gray-500 dark:text-gray-400">
            No hay relevamientos pendientes
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {items.map((item) => {
              const read = isRead(item.id);
              const location = notificationLocation(item);
              const checked = selected.has(item.id);
              return (
                <li
                  key={item.id}
                  className={`${
                    read ? 'bg-white dark:bg-gray-900' : 'bg-accent-50/60 dark:bg-accent-900/20'
                  }`}
                >
                  <div className="flex items-start gap-3 px-4 py-3">
                    <input
                      type="checkbox"
                      className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={checked}
                      onChange={() => toggleOne(item.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Seleccionar ${notificationTitle(item)}`}
                    />
                    <button
                      type="button"
                      onClick={() => openItem(item.id)}
                      className="flex-1 min-w-0 text-left hover:opacity-90"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p
                            className={`text-sm truncate ${
                              read
                                ? 'font-medium text-gray-700 dark:text-gray-300'
                                : 'font-semibold text-gray-900 dark:text-white'
                            }`}
                          >
                            {notificationTitle(item)}
                          </p>
                          {location && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                              {location}
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          {!read && (
                            <span className="inline-block w-2 h-2 rounded-full bg-primary-500 mb-1" />
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {item.estadoNombre || 'Abierto'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {relativeTime(item.fechaCreacion || item.fecha)}
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
